import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Dialog,
  Grid,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Button
} from '@material-ui/core';
import '@xyflow/react/dist/style.css';
import { Background, Controls, MiniMap, ReactFlow, useEdgesState, useNodesState } from '@xyflow/react';
import { getCustomCalculations, getPricingVisualizer, getProductV2Data, getRoles, getVisualizer } from '../../../Actions';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { CalculationVisualizerContext } from '../../../Context/CalculationVisualizerContextProvider';
import NewNodeModal from './NewNodeModal';
import EditNodeModal from './EditNodeModal';

export default function FlowView() {
  const { setGraph, refreshGraph, setRefreshGraph, isPromoFlow, setIsPromoFlow, includesPricing, setIsIncludesPricing, selectedOrientation, setSelectedOrientation, isNewNodeModalOpen, isEditNodeModalOpen, setSelectedNode } = useContext(CalculationVisualizerContext);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [productUUID, setProductUUID] = useState("");
  const [productData, setProductData] = useState();
  const [selectedOption, setSelectedOption] = useState("");
  const [isPromoFlowAvailable, setIsPromoFlowAvailable] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState([]); 
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const nodeTypes = useMemo(() => ({ default: CustomNode }), []);
  const edgeTypes = useMemo(() => ({ default: CustomEdge }), []);

  useEffect(() => {
    async function getPageData() {
      await getRoles()
        .then((result) => {
          setRoles(result.data.roleList ?? []);
        })
        .catch((err) => {
          setRoles([]);
        });
    }

    getPageData();
  }, [])

  useEffect(() => {
    async function refreshGraphData() {
      await getCustomCalculations()
        .then((result) => {
          setGraph(result.data);
        }).catch((err) => {
          setGraph(null);
        });
    }

    if (!refreshGraph) return;

    refreshGraphData()
    setRefreshGraph(false);
  }, [refreshGraph])

  async function getDataWithoutCalculations(selectedOrientation) {
    await getVisualizer(selectedOrientation)
      .then((result) => {
        const { nodes: newNodes, edges: newEdges } = result.data;
        
        setNodes([ ...newNodes ]);
        setEdges([ ...newEdges ]);
      })
      .catch((err) => {
        setNodes([]);
        setEdges([]);
      });
  }

  async function getDataWithCalculations(productUUID, selectedRole, selectedOption, selectedOrientation) {
    await getPricingVisualizer(productUUID, selectedRole, selectedOption, selectedOrientation)
      .then((result) => {
        const { nodes: newNodes, edges: newEdges } = result.data;

        setNodes([ ...newNodes ]);
        setEdges([ ...newEdges ]);
      })
      .catch((err) => {
        setNodes([]);
        setEdges([]);
      });
  }

  useEffect(() => {
    setNodes([]);
    setEdges([]);

    if (!selectedOption || !productUUID || !selectedRole) {
      getDataWithoutCalculations(selectedOrientation);
      setIsIncludesPricing(false);
    } else {
      getDataWithCalculations(productUUID, selectedRole, selectedOption, selectedOrientation);
      setIsIncludesPricing(true);
    };

    if (refreshGraph) setRefreshGraph(false);

    return () => {
      setSelectedNode(null);
    }
  }, [selectedOption, selectedOrientation, selectedRole, refreshGraph])

  useEffect(() => {
    if (includesPricing && nodes?.[0]?.data?.nodeData?.pricingData?.promotion) {
      setIsPromoFlowAvailable(true);
      return;
    }

    setIsPromoFlowAvailable(false);
    setIsPromoFlow(false);
  }, [nodes])

  const getProduct = async (productUUID, roleId) => {
    if (!productUUID || !roleId) return;

    await getProductV2Data(`product/v2/${productUUID}/${roleId}?calc=V2`)
      .then(result => {
        setProductData({ ...result.data.product });
      })
      .catch(err => {
        setProductData(undefined);
      })
  }

  const nodeColor = (node) => {
    if (['start', 'end'].includes(node.id)) {
      return node.id === 'start' ? '#388E3C' : '#f44336';
    }
    
    return '#999999';
  }

  return (
    <Paper style={{ height: '80vh', padding: '16px' }}>
      <Paper style={{ width: "350px", position: 'absolute', padding: '10px', zIndex: 1000 }}>
        <Grid container spacing={2} style={{ width: "100%", zIndex: 50, position: "relative" }}>
          {/* <Grid item xs={12}>
            <ToggleButtonGroup
              value={selectedOrientation}
              exclusive
              size='small'
              onChange={(e, newValue) => {
                if (!newValue) return;

                setSelectedOrientation(newValue)
              }}
              aria-label="text alignment"
            >
              <ToggleButton value='h' aria-label="horizontal" style={selectedOrientation === 'h' ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                Horizontal
              </ToggleButton>
              <ToggleButton value='v' aria-label="vertical" style={selectedOrientation === 'v' ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                Vertical
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid> */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="filled">
              <InputLabel shrink id="role-list-label">Selected Role</InputLabel>
              <Select
                value={selectedRole ?? ""}
                displayEmpty
                id="role-list-label"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <MenuItem value="" disabled>Please select one of the following</MenuItem>
                {
                  roles?.map(role => {
                    return <MenuItem key={role.role_id} value={role.role_id}>{role.role_name}</MenuItem>
                  })
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="filled"
              label="Product UUID"
              placeholder="Enter product UUID"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              value={productUUID}
              onChange={(e) => setProductUUID(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <Button 
              variant="contained" 
              style={{color: "#28C4FC", backgroundColor: "#142E3E"}}
              disabled={!selectedRole || !productUUID}
              onClick={() => getProduct(productUUID, selectedRole)}
            >
              Get Product
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button 
              variant="contained" 
              disabled={!productData}
              onClick={() => {
                setProductUUID("");
                setProductData(undefined);
                setSelectedOption("");
              }}
            >
              Clear Product
            </Button>
          </Grid>
          { productData?.options?.length ? (
              <Grid item xs={12}>
                <FormControl fullWidth variant="filled">
                  <InputLabel shrink id="option-list-label">Selected Option</InputLabel>
                  <Select
                    value={selectedOption ?? ""}
                    displayEmpty
                    id="option-list-label"
                    onChange={(e) => setSelectedOption(e.target.value)}
                  >
                    <MenuItem value="" disabled>Please select one of the following</MenuItem>
                    { 
                      productData.options.map(option => {

                        let optionDetails = "";

                        if (option.pricing.retail_price) {
                          optionDetails = "$" + option.pricing.retail_price.toFixed(2);
                        }

                        if (option.is_promo_active) {
                          optionDetails = "$" + option.promotion_pricing.retail_price.toFixed(2) + " (Promo)";
                        }

                        if (option.is_discontinued) {
                          optionDetails = "Discontinued";
                        } else if (option.is_call_for_price) {
                          optionDetails = "Call For Price";
                        }

                        if (option.uom) {
                          optionDetails += optionDetails !== "" ? ` - ${option.uom}` : option.uom;
                        }

                        if (option.basis_cd) {
                          optionDetails += optionDetails !== "" ? ` - ${option.basis_cd}` : option.basis_cd;
                        }

                        return (
                          <MenuItem key={option.cost_id} value={option.cost_id}>
                            <Typography variant="body2" color={option.is_discontinued ? "error" : "inherit"}>
                              <b>{option.color_name}</b>
                              <br></br>
                              { option.distributor ? (
                                  <>
                                    {option.distributor}
                                    <br></br>
                                  </>
                                ) : undefined
                              }
                              { option.fob_origin ? (
                                  <>
                                    {option.fob_origin}
                                    <br></br>
                                  </>
                                ) : undefined
                              }
                              <span style={{color: option.is_promo_active ? "#f44336" : "inherit"}}>{optionDetails}</span>  
                            </Typography>
                          </MenuItem>
                        )
                      })
                    }
                  </Select>
                </FormControl>
              </Grid>
            ) : undefined
          }
          { isPromoFlowAvailable && (
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={isPromoFlow?.toString()}
                exclusive
                size='small'
                onChange={(e, newValue) => {
                  if (newValue === null) return;

                  setIsPromoFlow(JSON.parse(newValue))
                }}
                aria-label="text alignment"
              >
                <ToggleButton value='false' aria-label="Standard" style={!isPromoFlow ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                  Standard
                </ToggleButton>
                <ToggleButton value='true' aria-label="Promotion" style={isPromoFlow ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                  Promotion
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}
        </Grid>
      </Paper>
      <Box style={{ height: '100%', width: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          nodesDraggable={false}
        >
          <Background />
          <Controls />
          <MiniMap nodeColor={nodeColor} zoomable pannable/>
        </ReactFlow>

        {isEditNodeModalOpen ? (
            <EditNodeModal />
          ) : undefined
        }
        {isNewNodeModalOpen ? (
            <NewNodeModal />
          ) : undefined
        }
      </Box>
    </Paper>
  );
};
